import React, { useCallback, useMemo, useState } from 'react'
import pick from 'lodash/pick'
import debounce from 'lodash/debounce'
import { v4 as uuidv4 } from 'uuid'

import { useGetProfile } from '@core/react-query/features/profile'
import { localizedStrings } from '@core/strings'
import {
  isSameParticipantsWithSameRole,
  uniqKeyFromExternalUser,
  ParticipantEntityType,
  ParticipantType,
  briefingsGenericHooksByEntityType,
} from '@core/modules/briefings'
import { Checklist } from '@core/entities/checklist/Checklist'
import { useProjectUsers } from '@core/react-query/features/projects/project'

import { Palette } from '@core/theme/color-palette-siteassist'
import { useModal } from '@common/GlobalModal'
import { Box, Flex, Text } from '@fundamentals'
import {
  Search as SearchIcon,
  Add as AddIcon,
  QrCode as QrCodeIcon,
  DeleteOutline as DeleteOutlineIcon,
  Close as CrossIcon,
} from '@mui/icons-material'
import {
  Divider,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tab,
  Tabs,
  InputAdornment,
  CircularProgress,
} from '@common/material'
import {
  BriefingAddExternalUserModal,
  BriefingQRShareModal,
  BriefingUserCard,
} from '@modules/briefings'
import { ParticipantListItem } from '@core/entities/participant/ParticipantListItem'
import { joinWithDelimiter } from '@core/utils/string'
import { ProjectUser } from '@core/entities/user'
import { Profile } from '@core/entities/profile'
import { Permit } from '@core/entities/permit/Permit'
import { PermitTemplateRole } from '@core/entities/permit/PermitTemplateRoles'

// NOTE: Same user can have multiple roles. In that case there would be
// multiple participants with the same user id and different role ids
export type ParticipantLocal = ParticipantType & {
  localId?: string
}

type BriefingParticipantsPickerProps = {
  participants: ParticipantLocal[]
  onChange: (next: ParticipantLocal[]) => void
  // Roles the participants can be briefed on — fetched by the caller: entity
  // roles in the post-creation modal, template roles in the create-permit flow.
  roles?: Array<
    Pick<PermitTemplateRole, 'id' | 'name' | 'groupIds' | 'isBriefingAllowed'>
  >
  projectId?: number
  organisationId?: number
  // Post-creation (modal) flow only — provides previously-briefed data, the
  // external participants modal and the checklist QR share. The in-flow
  // participants stage collects internal users only.
  entity?: Permit | Checklist
  entityType?: ParticipantEntityType
  showAddExternalUser?: boolean
}

export const BriefingParticipantsPicker: React.FC<
  BriefingParticipantsPickerProps
> = ({
  participants: selectedParticipants,
  onChange,
  roles,
  projectId,
  organisationId = 0,
  entity,
  entityType = 'PERMIT',
  showAddExternalUser = false,
}) => {
  const { showModal } = useModal()
  const { data: profile } = useGetProfile()

  const [activeTab, setActiveTab] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRoleId, setSelectedRoleId] = useState<number | undefined>(
    undefined,
  )
  const [filteringRoleId, setFilteringRoleId] = useState<number | undefined>(
    undefined,
  )

  const briefingRoles = roles?.filter((role) => !!role.isBriefingAllowed)
  const externalUserRoles =
    briefingRoles?.filter((role) => !role.groupIds?.length) ?? []
  const canAddExternalUser = showAddExternalUser && !!externalUserRoles.length
  const selectedRole = roles?.find((role) => role.id === selectedRoleId)

  const { isIsolated, companyIds } = Profile.getOrganisationIsolation(
    profile,
    organisationId,
  )

  const { data: projectUsersPage, isLoading: isLoadingProjectUsers } =
    useProjectUsers(
      {
        projectId,
        size: 1000,
        memberStatus: 'ACTIVE',
        term: searchTerm,
        authGroupsIds: selectedRole?.groupIds,
        companyIds: isIsolated ? companyIds?.map(String) : undefined,
      },
      { enabled: !!projectId },
    )
  const projectUsers = projectUsersPage?.pages?.[0]?.content ?? []

  const { useGetEntityRecentParticipants } =
    briefingsGenericHooksByEntityType[entityType]
  const {
    data: participantsRecent = [],
    isLoading: isLoadingParticipantsRecent,
  } = useGetEntityRecentParticipants(
    {
      term: searchTerm,
      projectId,
      authGroupsIds: selectedRole?.groupIds,
    },
    { enabled: !!projectId },
  )

  // Active participants of the entity, for participantId reuse and the
  // "last signed on" hint. Internal users are keyed by user id, externals by
  // their name+company key.
  const activeParticipantsByKey = useMemo(() => {
    const byKey = new Map<
      string | number,
      { participantId: number; lastSignedOn: string }
    >()
    entity?.getActiveParticipants()?.forEach((participant) => {
      byKey.set(participant.userId ?? uniqKeyFromExternalUser(participant), {
        participantId: participant.id,
        lastSignedOn: participant.lastSignedOn,
      })
    })
    return byKey
  }, [entity])

  const isSelectedWithSelectedRole = (key: string | number) =>
    selectedParticipants.some(
      (participant) =>
        (participant.userId ?? uniqKeyFromExternalUser(participant)) === key &&
        participant.roleId === selectedRoleId,
    )

  const tabUsers = (activeTab === 0 ? projectUsers : participantsRecent).filter(
    (user: ProjectUser | ParticipantListItem) => {
      const userId = user instanceof ProjectUser ? user.id : user.userId
      return (
        // Not the current user
        userId !== profile?.id &&
        // Not already added with the selected role
        !isSelectedWithSelectedRole(userId ?? uniqKeyFromExternalUser(user))
      )
    },
  )
  const tabUsersLoading =
    activeTab === 0 ? isLoadingProjectUsers : isLoadingParticipantsRecent

  const filteringRoles = roles?.filter((role) =>
    selectedParticipants.some((participant) => participant.roleId === role.id),
  )

  const selectedParticipantsFiltered = filteringRoleId
    ? selectedParticipants.filter(
        (participant) => participant.roleId === filteringRoleId,
      )
    : selectedParticipants

  const roleNameById: Record<number, string> = {}
  roles?.forEach((role) => {
    roleNameById[role.id] = role.name
  })

  const debouncedSearch = useCallback(
    debounce((term: string) => setSearchTerm(term), 300),
    [],
  )

  const handleAddExternalUser = (user) => {
    onChange([
      {
        ...user,
        localId: uuidv4(),
        participantId: activeParticipantsByKey.get(
          uniqKeyFromExternalUser(user),
        )?.participantId,
      },
      ...selectedParticipants.filter(
        (participant) =>
          // If user adds the same external user again with the same role,
          // we just move it to the top and filter out the old one. Dedupe
          // against the role picked in the external-user modal (user.roleId),
          // which may differ from the picker's selected role.
          participant.userId ||
          !(
            uniqKeyFromExternalUser(participant) ===
              uniqKeyFromExternalUser(user) &&
            participant.roleId === user.roleId
          ),
      ),
    ])
  }

  const handleSelectParticipant = (user: ProjectUser | ParticipantListItem) => {
    const userId = user instanceof ProjectUser ? user.id : user.userId
    const uniqKey = uniqKeyFromExternalUser(user)

    onChange([
      {
        ...pick(user, ['firstName', 'lastName', 'company']),
        userId,
        roleId: selectedRoleId,
        localId: uuidv4(),
        participantId: activeParticipantsByKey.get(userId ?? uniqKey)
          ?.participantId,
      },
      ...selectedParticipants.filter((participant) => {
        const isSameRole = participant.roleId === selectedRoleId

        // Filter out if same user and same role
        return participant.userId
          ? !(participant.userId === userId && isSameRole)
          : !(uniqKeyFromExternalUser(participant) === uniqKey && isSameRole)
      }),
    ])
  }

  const handleRemoveSelectedParticipant = (participant: ParticipantLocal) => {
    onChange(
      selectedParticipants.filter(
        (selectedParticipant) =>
          !isSameParticipantsWithSameRole(selectedParticipant, participant),
      ),
    )
  }

  return (
    <Flex flex={1} flexDirection='column' minHeight={0}>
      {/* minHeight 0 lets the columns shrink below their content so the
          lists scroll — without it the Card host (overflow hidden) clips. */}
      <Flex flex={1} minHeight={0}>
        <Box
          sx={{
            flex: 3,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ m: 3, mb: 2 }}>
            {briefingRoles?.length === 0 ? (
              <Text fontSize={14} color='text.secondary'>
                {localizedStrings.noBriefingRolesAvailable}
              </Text>
            ) : (
              <FormControl fullWidth>
                <InputLabel>{localizedStrings.role}</InputLabel>
                <Select
                  value={selectedRoleId ?? ''}
                  label={localizedStrings.role}
                  onChange={(e) => setSelectedRoleId(e.target.value as number)}
                >
                  {briefingRoles?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {!selectedRoleId && !!briefingRoles?.length && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
              }}
            >
              <Text fontSize={14} color='text.secondary' textAlign='center'>
                {localizedStrings.selectRoleToAddParticipants}
              </Text>
            </Box>
          )}

          {!!selectedRoleId && (
            <>
              <Box sx={{ ml: 3, mr: 3, mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_event, newValue) => setActiveTab(newValue)}
                >
                  <Tab
                    label={localizedStrings.newUser}
                    sx={{
                      textTransform: 'none',
                      '&:focus': { outline: 'none' },
                    }}
                  />
                  <Tab
                    label={localizedStrings.previouslyAdded}
                    sx={{
                      textTransform: 'none',
                      '&:focus': { outline: 'none' },
                    }}
                  />
                </Tabs>

                <Divider sx={{ mb: 2 }} />

                <TextField
                  fullWidth
                  size='small'
                  placeholder={localizedStrings.search}
                  onChange={(e) => debouncedSearch(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, pt: 0 }}>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {tabUsers?.length > 0 ? (
                    tabUsers.map((tabUser) => {
                      const tabUserKey =
                        tabUser instanceof ProjectUser
                          ? tabUser.id
                          : tabUser.userId ?? uniqKeyFromExternalUser(tabUser)

                      return (
                        <BriefingUserCard
                          key={tabUser.id}
                          title={`${tabUser.firstName} ${tabUser.lastName}`}
                          subtitle={joinWithDelimiter([
                            tabUser.company,
                            tabUser.email,
                          ])}
                          lastSignedOn={
                            activeParticipantsByKey.get(tabUserKey)
                              ?.lastSignedOn
                          }
                          onClick={() => handleSelectParticipant(tabUser)}
                        />
                      )
                    })
                  ) : tabUsersLoading ? (
                    <CircularProgress size={24} sx={{ alignSelf: 'center' }} />
                  ) : (
                    <Text
                      fontSize={14}
                      color='text.secondary'
                      textAlign='center'
                    >
                      {localizedStrings.noUsersFound}
                    </Text>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            flex: 2,
            bgcolor: Palette.Grey[50],
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box m={3} mb={2}>
            <Flex justifyContent='space-between' alignItems='center' mb={2}>
              <Text fontSize={14} fontWeight={500}>
                {localizedStrings.selectedParticipants}
              </Text>
              <Text fontSize={12} color='text.secondary'>
                {selectedParticipants.length} {localizedStrings.added}
              </Text>
            </Flex>

            <Flex sx={{ gap: 0.5, flexWrap: 'wrap' }}>
              {filteringRoles?.map((role) => {
                const isSelected = filteringRoleId === role.id

                if (!!filteringRoleId && !isSelected) {
                  return null
                }

                return (
                  <Button
                    key={role.id}
                    variant='outlined'
                    onClick={() => {
                      setFilteringRoleId((prevRoleId) =>
                        prevRoleId === role.id ? undefined : role.id,
                      )
                    }}
                  >
                    <Text fontSize={13} fontWeight={500} color='primary.main'>
                      {role.name}
                    </Text>

                    {isSelected && (
                      <CrossIcon sx={{ ml: 0.5, mr: -1, fontSize: 20 }} />
                    )}
                  </Button>
                )
              })}
            </Flex>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 3, pt: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedParticipantsFiltered.map((participant) => (
                <BriefingUserCard
                  key={`${
                    participant.sessionParticipantId ??
                    participant.userId ??
                    participant.participantId ??
                    participant.localId
                  }_${participant.roleId}`}
                  title={`${participant.firstName} ${participant.lastName}`}
                  subtitle={joinWithDelimiter([
                    roleNameById[participant.roleId],
                    participant.company,
                  ])}
                  Icon={
                    <IconButton
                      sx={{ mr: -1, mt: -1 }}
                      onClick={() =>
                        handleRemoveSelectedParticipant(participant)
                      }
                    >
                      <DeleteOutlineIcon color='error' />
                    </IconButton>
                  }
                  lastSignedOn={
                    activeParticipantsByKey.get(
                      participant.userId ??
                        uniqKeyFromExternalUser(participant),
                    )?.lastSignedOn
                  }
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Flex>

      {entity && (canAddExternalUser || entity instanceof Checklist) && (
        <>
          <Divider />
          <Flex gap={1} sx={{ pt: 2, pb: 2, pl: 3, pr: 3 }}>
            {canAddExternalUser && (
              <Button
                variant='outlined'
                startIcon={<AddIcon />}
                color='primary'
                onClick={() =>
                  showModal(
                    <BriefingAddExternalUserModal
                      roles={externalUserRoles}
                      onAddExternalUser={handleAddExternalUser}
                    />,
                  )
                }
              >
                {localizedStrings.addExternalUser}
              </Button>
            )}
            {entity instanceof Checklist && (
              <Button
                variant='outlined'
                startIcon={<QrCodeIcon />}
                color='primary'
                onClick={() =>
                  showModal(<BriefingQRShareModal checklist={entity} />)
                }
              >
                {localizedStrings.addViaQrCode}
              </Button>
            )}
          </Flex>
        </>
      )}
    </Flex>
  )
}
